import { get } from 'lodash';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { withNamespaces } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';

import { handleApiErrors } from '../../api/axiosInstance';
import ReportsAPI from '../../api/reports';
import ReportForm from '../../components/Forms/ReportForm';
import DeleteReportConfirmModal from '../../components/Modal/DeleteReportConfirmModal';
import GenericModal from '../../components/Modal/GenericModal';
import ReportsTable from '../../components/Table/ReportsTable';
import Toast from '../../components/Toast';
import PageContainer from '../../containers/PageContainer';
import { INTERNAL_LINKS } from '../../enum';
import withAuthentication from '../../hocs/withAuthentication';
import usePaginatedFiltersQuery from '../../hooks/queries/usePaginatedFiltersQuery';
import useModal from '../../hooks/useModal';
import { formatPageTitle } from '../../utils/common';

const ReportsManagement = props => {
  const { t, history } = props;
  const queryClient = useQueryClient();
  const [reportToDelete, setReportToDelete] = useState();
  const [isEditModalVisible, openEditModal, closeEditModal, editModalProps] = useModal();

  const {
    query: reportsQuery,
    paginationConfig,
    handlePageChange,
    handlePageSizeChange,
  } = usePaginatedFiltersQuery({
    staleTime: Infinity,
    placeholderData: {
      totalCount: 0,
      documents: [],
    },
    queryKey: ['fetchReports'],
    queryFn: () =>
      new ReportsAPI().fetchReports(
        undefined,
        paginationConfig.current,
        paginationConfig.pageSize,
        JSON.stringify({ name: 1 }),
      ),
    select: ({ documents, totalCount }) => ({
      documents,
      totalCount,
    }),
  });

  const deleteReportMutation = useMutation(reportId => new ReportsAPI().deleteReport(reportId), {
    onSuccess: () => {
      setReportToDelete();
      queryClient.invalidateQueries(['fetchReports'], { exact: false });
      Toast({
        type: 'open',
        message: t('reportDeleteSuccess'),
      });
      history.push(INTERNAL_LINKS.REPORTS_MANAGEMENT);
    },
    onError: error => {
      handleApiErrors(error.response, () => {
        Toast({
          type: 'error',
          message: error.message,
        });
      });
    },
  });

  const transformValues = values => {
    const {
      dashboardId,
      sendGroupIdAsString = false,
      companyId,
      userId,
      groupId,
      visibleGlobally = false,
      visibleOnReportsPage = false,
      tags = [],
      allowedRoles = [],
      ...rest
    } = values;

    return {
      visibleGlobally,
      visibleOnReportsPage,
      ...rest,
      tags: tags.map(tag => tag._id),
      settings: {
        dashboardId,
        sendGroupIdAsString,
        companyId,
        userId,
        groupId,
      },
      allowedRoles,
    };
  };

  const createReportMutation = useMutation(
    values => new ReportsAPI().createReport(transformValues(values)),
    {
      onError: error => handleApiErrors(error.response),
      onSuccess: () => {
        queryClient.invalidateQueries(['fetchReports'], { exact: false });
        closeEditModal();
        Toast({
          type: 'open',
          message: t('createMetabaseReportSuccess'),
        });
      },
    },
  );

  const updateReportMutation = useMutation(
    values =>
      new ReportsAPI().updateReport({
        data: transformValues(values),
        reportId: get(editModalProps, 'report._id'),
      }),
    {
      onError: error => handleApiErrors(error.response),
      onSuccess: () => {
        queryClient.invalidateQueries(['fetchReports'], { exact: false });
        closeEditModal();
        Toast({
          type: 'open',
          message: t('updateMetabaseReportSuccess'),
        });
      },
    },
  );

  const handleFormSubmit = async values => {
    const mutation = !!get(editModalProps, 'report._id')
      ? updateReportMutation
      : createReportMutation;

    return mutation.mutateAsync(values);
  };
  const isUpdate = !!get(editModalProps, 'report._id');

  return (
    <PageContainer
      title={t('reportsManagement')}
      pageActionText={t('createNewReport')}
      onPageActionClick={() => openEditModal({ title: t('createNewReport') })}
    >
      <Helmet>
        <title>{formatPageTitle(t('reportsManagement'))}</title>
      </Helmet>

      <ReportsTable
        t={t}
        loading={reportsQuery.isFetching || deleteReportMutation.isLoading}
        dataSource={reportsQuery.data.documents || []}
        onEditReportClick={data => openEditModal({ title: t('updateReport'), report: data })}
        onDeleteReportClick={id => setReportToDelete(id)}
        onChange={({ current }) => handlePageChange(current)}
        pagination={{
          pageSize: paginationConfig.pageSize,
          total: paginationConfig.total,
          current: paginationConfig.current,
          onShowSizeChange: handlePageSizeChange,
        }}
      />

      <DeleteReportConfirmModal
        t={t}
        visible={!!reportToDelete}
        loading={deleteReportMutation.isLoading}
        onConfirm={() => deleteReportMutation.mutateAsync(reportToDelete)}
        onCancel={() => setReportToDelete()}
      />

      <GenericModal
        {...editModalProps}
        centered
        footer={null}
        visible={isEditModalVisible}
        onCancel={closeEditModal}
        closable={!createReportMutation.isLoading && !updateReportMutation.isLoading}
      >
        <ReportForm
          t={t}
          key={get(editModalProps, 'report._id')}
          isUpdate={isUpdate}
          onCancel={closeEditModal}
          loading={createReportMutation.isLoading || updateReportMutation.isLoading}
          disabled={createReportMutation.isLoading || updateReportMutation.isLoading}
          onFinish={handleFormSubmit}
          initialValues={{
            name: get(editModalProps, 'report.name'),
            visibleGlobally: get(editModalProps, 'report.visibleGlobally'),
            visibleOnReportsPage: get(editModalProps, 'report.visibleOnReportsPage'),
            dashboardId: get(editModalProps, 'report.settings.dashboardId'),
            sendGroupIdAsString: get(editModalProps, 'report.settings.sendGroupIdAsString'),
            sourceType: get(editModalProps, 'report.sourceType'),
            internalName: get(editModalProps, 'report.internalName'),
            companyId: get(editModalProps, 'report.settings.companyId'),
            groupId: get(editModalProps, 'report.settings.groupId'),
            userId: get(editModalProps, 'report.settings.userId'),
            tags: get(editModalProps, 'report.tags') || [],
            allowedRoles: get(editModalProps, 'report.allowedRoles') || [],
          }}
        />
      </GenericModal>
    </PageContainer>
  );
};

export default withNamespaces()(withAuthentication(ReportsManagement));
